import type { PageContextClient } from 'vike/types';
import * as Sentry from '@sentry/vue';
import { initConnectionStore } from '../utils/initConnectionStore';
import { initEventId } from '../utils/initEventId';
import { useLanguageStore } from '../languageStore';
import {
  ApolloLink,
  FetchResult,
  fromPromise,
  Observable,
  ServerError,
} from '@apollo/client/core';
import { turnstile } from '@vp/vue-components';
import { initRuntimeConfig } from '../composables/useRuntimeConfig';

const isTurnstileChallenge = (err: any): boolean => {
  const e = err as ServerError;
  const header = e.response?.headers?.get('X-Challenge');
  const body = (e.result ?? {}) as any;
  return e?.statusCode === 403 && (header === 'turnstile' || body.require_challenge);
};

const createHumanMiddleware = () => {
  const turnstileStore = turnstile.useTurnstileStore();
  turnstileStore.warmup().catch(() => {});

  return new ApolloLink((operation, forward) => {
    return new Observable<FetchResult>(observer => {
      if (turnstileStore.humanToken) {
        operation.setContext(({ headers = {} }) => ({
          headers: {
            ...headers,
            'X-VP-Human-Token': turnstileStore.humanToken!,
          },
        }));
      }

      const sub = forward(operation).subscribe({
        next: v => observer.next(v),
        complete: () => observer.complete(),
        error: async error => {
          if (isTurnstileChallenge(error)) {
            fromPromise(turnstileStore.solve())
              .flatMap(() => forward(operation))
              .subscribe({
                next: v => observer.next(v),
                complete: () => observer.complete(),
                error: err => observer.error(err),
              });

            return;
          }

          observer.error(error);
        },
      });

      return () => sub.unsubscribe();
    });
  });
};

export const onBeforeRenderClient = async (pageContext: PageContextClient) => {
  const { app } = pageContext;
  if (!app) {
    return;
  }

  hydratePinia(pageContext);
  const { turnstile } = await initRuntimeConfig();

  const middleware = [];
  if (turnstile?.enabled) {
    middleware.push(createHumanMiddleware());
  }

  initConnectionStore(pageContext, middleware);
  initEventId(pageContext);

  // Awaited before the first render, or a one-shot translation freezes the raw msgid.
  const languageStore = useLanguageStore();
  const urlLocale = pageContext.locale;
  const locale = urlLocale ?? languageStore.current;

  if (locale) {
    try {
      await languageStore.setLanguage(locale, Boolean(urlLocale));
    } catch (error) {
      Sentry.captureException(error);
    }
  }
};

const hydratePinia = (pageContext: PageContextClient) => {
  if (!pageContext.isHydration) {
    return;
  }

  const { pinia, _piniaInitialState } = pageContext;
  if (!pinia || !_piniaInitialState) {
    return;
  }

  pinia.state.value = _piniaInitialState;
};
